import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'

import { successStaleTime } from '@/shared/api/utils/query'
import { useCurrentTenant } from '@/shared/auth/store'
import { time } from '@/shared/datetime/utils/time'

import { AIR_ROOT_KEY } from '../query-keys'
import { getPolicyDetails, getPolicyList } from './api'

/**
 * Query keys for Policy Management.
 *
 * Tuples are byte-identical to the pre-split `airKeys.polic*` — the split moved
 * where they are declared, never what they produce.
 */
export const policyKeys = {
	all: [...AIR_ROOT_KEY, 'policies'] as const,
	policies_list: (tenant: string) => [...policyKeys.all, tenant] as const,
	policy_details: (tenant: string, policyId: string) =>
		[...policyKeys.all, tenant, policyId] as const,
}

/**
 * Query options for Policy Management
 */
export const policyQueries = {
	policies_list: (tenant: string) =>
		queryOptions({
			queryKey: policyKeys.policies_list(tenant),
			queryFn: ({ signal }) => getPolicyList(tenant, signal),
			staleTime: ({ state }) => successStaleTime(state, 2, 'm'),
			gcTime: time(3, 'm'),
			select: (data) =>
				[...data.rules].sort((a, b) =>
					a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
				),
			enabled: !!tenant,
		}),
	policy_details: (tenant: string, policyId: string) =>
		queryOptions({
			queryKey: policyKeys.policy_details(tenant, policyId),
			queryFn: ({ signal }) => getPolicyDetails(tenant, policyId, signal),
			gcTime: time(3, 'm'),
		}),
}

/**
 * Use policies list
 */
export const usePoliciesList = () => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(policyQueries.policies_list(tenant))
}

/**
 * Use policy details
 * @param policyId - The policy ID
 * @returns The policy details
 */
export const usePolicyDetails = (policyId: string) => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(policyQueries.policy_details(tenant, policyId))
}
